import React, { useState, useEffect, useMemo } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { X, Check } from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount || 0);
};

const EditPriceModal = ({ isOpen, onClose, billItem, isGstBill, isPurchaseBill, onSave }) => {
  const { t } = useLang();

  const [price, setPrice] = useState(() => {
    if (!billItem) return '';
    const g = parseFloat(billItem.gstRate || billItem.item?.gstRate || '0') || 0;
    const taxInc = billItem.taxIncluded || billItem.item?.taxIncluded || false;
    const initialPriceVal = taxInc
      ? (billItem.originalPrice * (1 + (g / 100)))
      : billItem.originalPrice;
    return initialPriceVal % 1 === 0 
      ? initialPriceVal.toString() 
      : initialPriceVal.toFixed(2);
  });
  const [discount, setDiscount] = useState('');
  const [discountType, setDiscountType] = useState(billItem?.discountType || billItem?.discount_type || 'rupee'); // 'rupee', 'percentage'
  const [gstRate, setGstRate] = useState(billItem?.item?.gstRate?.replace('%', '') || '0');
  const [hsnCode, setHsnCode] = useState(billItem?.item?.hsnSacCode || '');
  const [taxIncluded, setTaxIncluded] = useState(billItem?.item?.taxIncluded || false);
  const [qty, setQty] = useState(billItem?.quantity?.toString() || '1');

  useEffect(() => {
    if (billItem) {
      const g = parseFloat(billItem.gstRate || billItem.item?.gstRate || '0') || 0;
      const taxInc = billItem.taxIncluded || billItem.item?.taxIncluded || false;
      const initialPriceVal = taxInc
        ? (billItem.originalPrice * (1 + (g / 100)))
        : billItem.originalPrice;
      
      const formattedPrice = initialPriceVal % 1 === 0 
        ? initialPriceVal.toString() 
        : initialPriceVal.toFixed(2);

      setPrice(formattedPrice || '');
      setQty(billItem.quantity?.toString() || '1');
      
      const dt = billItem.discountType || billItem.discount_type || 'rupee';
      setDiscountType(dt);
      
      const rawGst = billItem.gstRate || billItem.item?.gstRate || '0';
      setGstRate(rawGst.toString().replace('%', ''));
      
      setHsnCode(billItem.hsnCode || billItem.item?.hsnSacCode || '');
      setTaxIncluded(taxInc);

      if (dt === 'rupee') {
        setDiscount(billItem.discountValue > 0 ? billItem.discountValue.toString() : '');
      } else {
        const perc = (billItem.discountValue / billItem.originalPrice) * 100;
        setDiscount(perc > 0 ? perc.toFixed(2) : '');
      }
    }
  }, [billItem]);

  const calculations = useMemo(() => {
    const p = parseFloat(price) || 0;
    const d = parseFloat(discount) || 0;
    const g = isGstBill ? (parseFloat(gstRate) || 0) : 0;
    
    let basePrice = p;
    if (taxIncluded) {
      basePrice = p / (1 + (g / 100));
    }

    let discountAmount = 0;
    if (discountType === 'rupee') {
      discountAmount = d;
    } else {
      discountAmount = (basePrice * d) / 100;
    }

    const taxableAmount = basePrice - discountAmount;
    const taxAmount = (taxableAmount * g) / 100;
    const finalTotal = taxableAmount + taxAmount;

    return { basePrice, discountAmount, taxableAmount, taxAmount, finalTotal };
  }, [price, discount, discountType, gstRate, taxIncluded, isGstBill]);

  if (!isOpen || !billItem) return null;

  const handleSaveClick = () => {
    const q = parseFloat(qty) || 1;
    onSave({
      ...billItem,
      price: calculations.taxableAmount,
      originalPrice: calculations.basePrice,
      discountValue: calculations.discountAmount,
      discountType: discountType,
      taxAmount: calculations.taxAmount * q,
      gstRate: `${gstRate}%`,
      hsnCode: hsnCode,
      taxIncluded: taxIncluded,
      quantity: q,
      item: {
        ...billItem.item,
        taxIncluded: taxIncluded,
        gstRate: `${gstRate}%`,
        hsnSacCode: hsnCode
      }
    });
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
           <div style={styles.headerLeft}>
              <h3 style={styles.title}>{billItem.item?.name}</h3>
              <span style={styles.subtitle}>{t(LangKeys.edit_price_dialog_title || 'Edit Price & Discount')}</span>
           </div>
           <button style={styles.closeBtn} onClick={onClose}><X size={20} /></button>
        </div>
        
        <div style={styles.content}>
           {/* PRICE FIELD */}
           <div style={styles.inputGroup}>
              <label style={styles.label}>{isPurchaseBill ? t(LangKeys.purchase_price) : t(LangKeys.sale_price)}</label>
              <div style={styles.inputWrapper}>
                 <span style={styles.inputPrefix}>₹</span>
                 <input 
                   style={styles.input} 
                   type="number" 
                   value={price} 
                   onChange={(e) => setPrice(e.target.value)} 
                 />
              </div>
           </div>

           {/* TAX INCLUDED TOGGLE */}
           {isGstBill && (
             <div style={styles.toggleRow}>
                <span>{t(LangKeys.tax_included)}</span>
                <div 
                   style={{...styles.toggle, ...(taxIncluded ? styles.toggleActive : {})}}
                   onClick={() => setTaxIncluded(!taxIncluded)}
                >
                   <div style={{...styles.toggleCircle, ...(taxIncluded ? styles.toggleCircleActive : {})}} />
                </div>
             </div>
           )}

           {/* QTY FIELD */}
           <div style={styles.inputGroup}>
              <label style={styles.label}>{t(LangKeys.quantity)}</label>
              <input 
                style={styles.inputBox} 
                type="number" 
                value={qty} 
                onChange={(e) => setQty(e.target.value)} 
              />
           </div>

           {/* GST DETAILS */}
           {isGstBill && (
             <div style={styles.row}>
                <div style={styles.inputGroup}>
                   <label style={styles.label}>{t(LangKeys.hsn_code)}</label>
                   <input style={styles.inputBox} value={hsnCode} onChange={(e) => setHsnCode(e.target.value)} />
                </div>
                <div style={styles.inputGroup}>
                   <label style={styles.label}>{t(LangKeys.gst_rate_percent)} (%)</label>
                   <input style={styles.inputBox} type="number" value={gstRate} onChange={(e) => setGstRate(e.target.value)} />
                </div>
             </div>
           )}

           {/* DISCOUNT */}
           <div style={styles.inputGroup}>
              <label style={styles.label}>{t(LangKeys.item_select_item_discount)}</label>
              <div style={styles.discountRow}>
                 <input 
                    style={{...styles.inputBox, flex: 1}} 
                    type="number" 
                    value={discount} 
                    onChange={(e) => setDiscount(e.target.value)} 
                    placeholder="0"
                 />
                 <div style={styles.discountTypeGroup}>
                    <button 
                      style={{...styles.typeBtn, ...(discountType === 'rupee' ? styles.typeBtnActive : {})}}
                      onClick={() => setDiscountType('rupee')}
                    >₹</button>
                    <button 
                      style={{...styles.typeBtn, ...(discountType === 'percentage' ? styles.typeBtnActive : {})}}
                      onClick={() => setDiscountType('percentage')}
                    >%</button>
                 </div>
              </div>
           </div>

           {/* SUMMARY ROW */}
           <div style={styles.summaryRow}>
              <div style={styles.sumItem}><span style={styles.sumLabel}>{t(LangKeys.rate)}</span><span style={styles.sumVal}>{formatCurrency(calculations.taxableAmount)}</span></div>
              <span style={styles.sumOp}>+</span>
              <div style={styles.sumItem}><span style={styles.sumLabel}>{t(LangKeys.tax)}</span><span style={styles.sumVal}>{formatCurrency(calculations.taxAmount)}</span></div>
              <span style={styles.sumOp}>=</span>
              <div style={styles.sumItem}><span style={styles.sumLabel}>{t(LangKeys.total)}</span><span style={{...styles.sumVal, color: '#1D4ED8'}}>{formatCurrency(calculations.finalTotal)}</span></div>
           </div>
        </div>

        <div style={styles.footer}>
           <button style={styles.saveBtn} onClick={handleSaveClick}>
              <Check size={18} /> <span>{t(LangKeys.save).toUpperCase()}</span>
           </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: '20px' },
  modal: { width: '100%', maxWidth: '440px', background: 'white', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
  header: { padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerLeft: { display: 'flex', flexDirection: 'column' },
  title: { fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 },
  subtitle: { fontSize: '12px', color: '#64748B', fontWeight: '600' },
  closeBtn: { border: 'none', background: 'transparent', cursor: 'pointer', color: '#94A3B8' },
  content: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 },
  label: { fontSize: '11px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase' },
  inputWrapper: { display: 'flex', alignItems: 'center', gap: '8px', background: '#F8FAFC', padding: '0 16px', borderRadius: '12px', border: '1px solid #E2E8F0', height: '48px' },
  inputPrefix: { fontSize: '16px', fontWeight: '700', color: '#64748B' },
  input: { border: 'none', background: 'transparent', outline: 'none', fontSize: '16px', fontWeight: '700', width: '100%' },
  inputBox: { height: '48px', padding: '0 16px', borderRadius: '12px', border: '1px solid #E2E8F0', background: '#F8FAFC', fontSize: '16px', fontWeight: '700', outline: 'none' },
  row: { display: 'flex', gap: '16px' },
  toggleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', fontWeight: '700', color: '#475569' },
  toggle: { width: '44px', height: '24px', borderRadius: '12px', background: '#E2E8F0', position: 'relative', cursor: 'pointer', transition: '0.3s' },
  toggleActive: { background: '#1D4ED8' },
  toggleCircle: { width: '18px', height: '18px', borderRadius: '9px', background: 'white', position: 'absolute', top: '3px', left: '3px', transition: '0.3s' },
  toggleCircleActive: { left: '23px' },
  discountRow: { display: 'flex', gap: '10px' },
  discountTypeGroup: { display: 'flex', background: '#F1F5F9', padding: '4px', borderRadius: '10px', gap: '4px' },
  typeBtn: { width: '36px', height: '36px', border: 'none', borderRadius: '8px', background: 'transparent', fontSize: '16px', fontWeight: '800', color: '#64748B', cursor: 'pointer' },
  typeBtnActive: { background: 'white', color: '#1D4ED8', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  summaryRow: { background: '#F8FAFC', padding: '16px', borderRadius: '16px', border: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  sumItem: { display: 'flex', flexDirection: 'column', gap: '2px' },
  sumLabel: { fontSize: '10px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase' },
  sumVal: { fontSize: '14px', fontWeight: '800', color: '#1E293B' },
  sumOp: { fontSize: '16px', fontWeight: '700', color: '#CBD5E1' },
  footer: { padding: '16px 24px', background: '#F8FAFC', borderTop: '1px solid #F1F5F9' },
  saveBtn: { width: '100%', height: '52px', borderRadius: '14px', border: 'none', background: '#1D4ED8', color: 'white', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }
};

export default EditPriceModal;
